import React, { Suspense, lazy, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./styles/app-responsive.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import RoleProtectedRoute from "./components/RoleProtectedRoute";
import PermissionRoute from "./components/PermissionRoute";
import PublicRoute from "./components/PublicRoute";
import Layout from "./shared/Layout";

/* ================= LAZY IMPORTS ================= */

const Home = lazy(() => import("./components/Home"));
const Register = lazy(() => import("./components/Register"));
const Contact = lazy(() => import("./components/Contact"));
const Login = lazy(() => import("./components/Login"));
const Logout = lazy(() => import("./components/Logout"));
const ForgotPassword = lazy(() => import("./components/ForgotPassword"));
const ResetPassword = lazy(() => import("./components/ResetPassword"));
const ForgotPasswordOtp = lazy(() => import("./components/ForgotPasswordOtp"));
const ForgotPasswordEmail = lazy(() =>
  import("./components/ForgotPasswordEmail")
);
const VerifyOtpReset = lazy(() =>
  import("./components/VerifyOtpReset")
);
const Profile = lazy(() => import("./components/Profile"));
const Unauthorized = lazy(() =>
  import("./components/Unauthorized")
);

const ReminderAlertListener = lazy(() =>
  import("./modules/Reminders/components/ReminderAlertListener")
);

/* ===== DASHBOARDS ===== */
const AdminDashboard = lazy(() =>
  import("./modules/Admins/components/AdminDashboard")
);
const AttendanceMap = lazy(() =>
  import("./modules/Admins/components/AttendanceMap")
);
const CustomerDashboard = lazy(() =>
  import("./modules/Customers/components/CustomerDashboard")
);
const EmployeeDashboard = lazy(() =>
  import("./modules/Employees/components/EmployeeDashboard")
);

/* ===== MODULES ===== */
const UserList = lazy(() =>
  import("./modules/Admins/components/UserList")
);
const RoleList = lazy(() =>
  import("./modules/Admins/components/RoleList")
);
const EntityList = lazy(() =>
  import("./modules/Admins/components/EntitityList")
);
const EntityDetails = lazy(() =>
  import("./modules/Admins/components/EntityDetails")
);
const Reminders = lazy(() =>
  import("./modules/Reminders/components/Reminders")
);
const Incidents = lazy(() =>
  import("./modules/Incidents/components/Incidents")
);

/* ================= BODY CLASS MANAGER ================= */

function BodyClassManager() {
  const location = useLocation();

  useEffect(() => {
    const blankPageRoutes = [
      "/login",
      "/register",
      "/contact",
      "/forgot-password",
      "/forgot-password/email",
      "/forgot-password/otp",
      "/forgot-password/verify-otp"
    ];

    if (blankPageRoutes.includes(location.pathname)) {
      document.body.className =
        "vertical-layout vertical-menu 1-column blank-page blank-page";
      document.body.setAttribute("data-col", "1-column");
    } else {
      document.body.className =
        "vertical-layout vertical-menu 2-columns fixed-navbar";
      document.body.setAttribute("data-col", "2-columns");
    }

    document.body.setAttribute("data-open", "click");
    document.body.setAttribute("data-menu", "vertical-menu");

    return () => {
      document.body.className = "";
      document.body.removeAttribute("data-open");
      document.body.removeAttribute("data-menu");
      document.body.removeAttribute("data-col");
    };
  }, [location.pathname]);

  return null;
}

/* ================= APP ================= */

function App() {
  return (
    <BrowserRouter>
      <BodyClassManager />

      <ToastContainer position="top-right" autoClose={3000} />

      <Suspense fallback={<div>Loading...</div>}>

        <ReminderAlertListener />

        <Routes>

          {/* ========= PUBLIC ROUTES ========= */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route path="/register" element={<Register />} />
          <Route path="/contact-us" element={<Contact />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/forgot-password/email"
            element={<ForgotPasswordEmail />}
          />
          <Route
            path="/forgot-password/otp"
            element={<ForgotPasswordOtp />}
          />
          <Route
            path="/forgot-password/verify-otp"
            element={<VerifyOtpReset />}
          />
          <Route
            path="/reset-password/:token"
            element={<ResetPassword />}
          />

          {/* ========= PROTECTED LAYOUT ========= */}
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/profile" element={<Profile />} />

            {/* ===== DASHBOARDS ===== */}
            <Route
              path="/admins/dashboard"
              element={
                <RoleProtectedRoute
                  allowedRoles={["superadmin", "admin", "manager"]}
                >
                  <AdminDashboard />
                </RoleProtectedRoute>
              }
            />

            <Route
              path="/admins/attendance-map"
              element={
                <RoleProtectedRoute allowedRoles={["superadmin"]}>
                  <AttendanceMap />
                </RoleProtectedRoute>
              }
            />

            <Route
              path="/customers/dashboard"
              element={
                <RoleProtectedRoute allowedRoles={["customer"]}>
                  <CustomerDashboard />
                </RoleProtectedRoute>
              }
            />

            <Route
              path="/employees/dashboard"
              element={
                <RoleProtectedRoute allowedRoles={["employee"]}>
                  <EmployeeDashboard />
                </RoleProtectedRoute>
              }
            />

            {/* ===== MODULE ROUTES ===== */}
            <Route
              path="/users/list"
              element={
                <PermissionRoute permissions={["users:read"]}>
                  <UserList />
                </PermissionRoute>
              }
            />

            <Route
              path="/roles/list"
              element={
                <PermissionRoute permissions={["roles:read"]}>
                  <RoleList />
                </PermissionRoute>
              }
            />

            <Route
              path="/entities/list"
              element={
                <PermissionRoute permissions={["entities:read"]}>
                  <EntityList />
                </PermissionRoute>
              }
            />
            <Route
              path="/entities/:id"
              element={
                <PermissionRoute permissions={["entities:read"]}>
                  <EntityDetails />
                </PermissionRoute>
              }
            />

            <Route
              path="/reminders"
              element={
                <PermissionRoute permissions={["reminders:read"]}>
                  <Reminders />
                </PermissionRoute>
              }
            />

            <Route
              path="/incidents"
              element={
                <PermissionRoute
                  permissions={["incidents:view-own", "view-subordinates"]}
                >
                  <Incidents />
                </PermissionRoute>
              }
            />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;